import { useEffect, useRef, useState } from "react";
import type { ClosingDoc, ClosingDocKind } from "@closing/shared";
import { api } from "../lib/api";
import { watchDealDocs } from "../lib/db";

const KIND_OPTIONS: { value: ClosingDocKind; label: string }[] = [
  { value: "deal_doc", label: "Deal documents" },
  { value: "term_sheet", label: "Term sheet" },
  { value: "intake_sheet", label: "Intake sheet" },
];

const KIND_BADGE: Record<string, { label: string; cls: string }> = {
  term_sheet: { label: "term sheet", cls: "bg-active-bg text-active" },
  intake_sheet: { label: "intake sheet", cls: "bg-[#fdf0dc] text-[#9a5b00]" },
  deal_doc: { label: "deal doc", cls: "bg-neutral-bg text-ink-soft" },
};

/**
 * Deal-document uploads: drag-drop or file picker → signed PUT to GCS.
 * PDF + DOCX only, 25 MB max (enforced server-side too). The kind selector
 * tags uploads — term sheets / intake sheets can be analyzed before the
 * deal documents exist.
 */
export default function DocumentsPanel({ dealId }: { dealId: string }) {
  const [docs, setDocs] = useState<(ClosingDoc & { id: string })[]>([]);
  const [kind, setKind] = useState<ClosingDocKind>("deal_doc");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => watchDealDocs(dealId, setDocs), [dealId]);

  async function handleFiles(files: FileList | File[]) {
    setError(null);
    for (const file of Array.from(files)) {
      setUploading((u) => [...u, file.name]);
      try {
        await api.uploadDoc(dealId, file, kind);
      } catch (err) {
        setError(`${file.name}: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setUploading((u) => u.filter((n) => n !== file.name));
      }
    }
  }

  async function open(docId: string) {
    try {
      const { url } = await api.getDocUrl(docId);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div>
      {/* What are you uploading? Applied to every file in the next drop. */}
      <div className="mb-3 flex items-center gap-2">
        <span className="eyebrow">Uploading:</span>
        <div className="flex overflow-hidden rounded-md border border-line">
          {KIND_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setKind(opt.value)}
              className={`px-3 py-1.5 font-mono text-[11px] transition-colors ${
                kind === opt.value ? "bg-ink text-white" : "bg-white text-muted hover:text-ink"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void handleFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInput.current?.click()}
        className={`cursor-pointer rounded-[10px] border-2 border-dashed px-6 py-10 text-center transition-colors ${
          dragging ? "border-signature bg-flag-bg/40" : "border-line-strong bg-white hover:border-muted"
        }`}
      >
        <p className="text-sm font-semibold text-ink">Drop deal documents here</p>
        <p className="mt-1 font-mono text-[11px] text-muted">
          or click to browse · PDF / DOCX · 25 MB max
        </p>
        <input
          ref={fileInput}
          type="file"
          multiple
          accept=".pdf,.docx"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) void handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {error && <p className="mt-3 font-mono text-[11px] text-flag">{error}</p>}

      <div className="mt-5 space-y-2">
        {uploading.map((name) => (
          <div
            key={name}
            className="flex items-center justify-between rounded-[10px] border border-line bg-paper px-4 py-2.5"
          >
            <span className="truncate text-[13px] text-muted">{name}</span>
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted">
              uploading…
            </span>
          </div>
        ))}
        {docs.map((d) => (
          <div
            key={d.id}
            className="group flex items-center justify-between gap-3 rounded-[10px] border border-line bg-white px-4 py-2.5"
          >
            <button
              onClick={() => void open(d.id)}
              className="min-w-0 flex-1 truncate text-left text-[13px] font-medium text-ink hover:text-signature"
              title={d.filename}
            >
              {d.filename}
            </button>
            <span
              className={`badge flex-none text-[10px] ${KIND_BADGE[d.kind ?? "deal_doc"]!.cls}`}
            >
              {KIND_BADGE[d.kind ?? "deal_doc"]!.label}
            </span>
            <span className="font-mono text-[10px] tabular-nums text-faint">
              {(d.sizeBytes / 1024 / 1024).toFixed(1)} MB
            </span>
            <span className="font-mono text-[10px] text-faint">{d.uploadedBy.split("@")[0]}</span>
            <button
              onClick={() => {
                if (window.confirm(`Delete "${d.filename}"?`)) {
                  void api.deleteDoc(d.id).catch((err) => setError(String(err)));
                }
              }}
              className="font-mono text-[11px] text-muted opacity-0 transition-opacity hover:text-flag group-hover:opacity-100"
              title="Delete document"
            >
              ×
            </button>
          </div>
        ))}
        {docs.length === 0 && uploading.length === 0 && (
          <p className="py-6 text-center font-mono text-[11px] text-faint">
            No documents uploaded yet.
          </p>
        )}
      </div>
    </div>
  );
}
